import { useRef } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import SaintDisclaimer from "@/components/SaintDisclaimerBlock";
import { RelatedPages } from "@/components/RelatedPages";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { buildBreadcrumbList } from "@/components/schemaConstants";
import { TreatmentCalendar } from "./saint-vs-neurostar/TreatmentCalendar";
import {
  comparisonRows,
  verdicts,
  keyDifferences,
  whyItMatters,
  faqs,
  sources,
  competitorNotice,
  heroTrustPoints,
} from "./saint-vs-neurostar/data";
import {
  Phone,
  Calendar,
  Target,
  FlaskConical,
  ArrowRight,
  ShieldCheck,
  Stethoscope,
  MapPin,
} from "lucide-react";

const trustIcons = [ShieldCheck, Stethoscope, FlaskConical];
const verdictIcons = [Calendar, Target, FlaskConical];

const SaintVsNeurostar = () => {
  const comparisonRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const scrollToComparison = () =>
    comparisonRef.current?.scrollIntoView({ behavior: "smooth" });
  const scrollToForm = () =>
    formRef.current?.scrollIntoView({ behavior: "smooth" });

  const crumbs = [
    { name: "Compare Treatments", href: "/compare-treatments" },
    { name: "SAINT® vs NeuroStar TMS", href: "/saint-vs-neurostar" },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <SEOHead
        title="SAINT® vs NeuroStar TMS | Neuro Wellness TMS"
        description="Compare SAINT® TMS to NeuroStar: 5 days vs 36 sessions, fMRI targeting, placebo-controlled results. Verified SAINT Provider in Coral Springs, FL."
        canonical="/saint-vs-neurostar"
        keywords="SAINT vs NeuroStar, SAINT TMS vs NeuroStar, accelerated TMS comparison, fMRI-guided TMS, NeuroStar alternative, SAINT TMS Coral Springs"
        schemaJson={[
          buildBreadcrumbList([{ name: "Home", href: "/" }, ...crumbs]),
          faqSchema,
        ]}
      />
      <Navbar />
      <main className="bg-background">
        {/* ── HERO ── */}
        <section className="relative overflow-hidden bg-gradient-to-b from-card to-background py-12 sm:py-16 lg:py-20">
          <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-accent/5 blur-3xl" />
          <div className="container-px relative mx-auto max-w-7xl">
            <Breadcrumbs crumbs={crumbs} className="mb-6" />
            <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <p className="font-headings text-sm font-bold uppercase tracking-wider text-accent">
                  SAINT® vs NeuroStar
                </p>
                <h1 className="mt-3 font-headings text-3xl font-bold leading-tight text-primary sm:text-4xl lg:text-5xl">
                  Why patients choose{" "}
                  <span className="text-accent">SAINT®</span> over NeuroStar.
                </h1>
                <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
                  NeuroStar is the TMS most clinics own: one session a day, five
                  days a week, for six weeks or more. SAINT® is what Stanford
                  built to replace that model — fMRI-guided targeting, 50
                  sessions in five days, and 79% remission in a
                  placebo-controlled trial.
                </p>
                <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={scrollToForm}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-accent px-7 py-3.5 text-base font-bold text-accent-foreground shadow-glow transition-opacity hover:opacity-90"
                  >
                    Request a Free Consultation
                    <ArrowRight className="h-5 w-5" />
                  </button>
                  <button
                    onClick={scrollToComparison}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-primary bg-transparent px-7 py-3.5 text-base font-bold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                  >
                    See the Side-by-Side
                  </button>
                </div>
                {/* Trust points */}
                <div className="mt-8 space-y-3">
                  {heroTrustPoints.map((t, i) => {
                    const Icon = trustIcons[i];
                    return (
                      <div
                        key={i}
                        className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-soft"
                      >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10">
                          <Icon className="h-5 w-5 text-accent" />
                        </div>
                        <div>
                          <p className="font-headings text-sm font-bold text-primary">
                            {t.title}
                          </p>
                          <p className="mt-0.5 text-sm text-muted-foreground leading-relaxed">
                            {t.text}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Eligibility form above the fold */}
              <div ref={formRef} className="lg:pl-4">
                <EligibilityCheckForm
                  source="saint-vs-neurostar-hero"
                  redirectTo="/thank-you-saint-google"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── COMPARISON TABLE ── */}
        <section ref={comparisonRef} className="bg-background py-12 sm:py-16">
          <div className="container-px mx-auto max-w-6xl">
            <p className="font-headings text-sm font-bold uppercase tracking-wider text-accent">
              Side-by-Side
            </p>
            <h2 className="mt-2 font-headings text-2xl font-bold text-primary sm:text-3xl">
              SAINT® vs NeuroStar® Advanced Therapy
            </h2>

            {/* Desktop table */}
            <div className="mt-8 hidden overflow-hidden rounded-2xl border border-border shadow-soft lg:block">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-card">
                    <th className="p-4 text-left font-headings text-sm font-bold text-primary">
                      Dimension
                    </th>
                    <th className="border-l-4 border-accent bg-accent/5 p-4 text-left font-headings text-sm font-bold text-accent">
                      SAINT®
                    </th>
                    <th className="p-4 text-left font-headings text-sm font-bold text-primary">
                      NeuroStar® Advanced Therapy
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, i) => (
                    <tr
                      key={i}
                      className="border-b border-border last:border-0"
                    >
                      <td className="p-4 align-top text-sm font-semibold text-primary">
                        {row.label}
                      </td>
                      <td className="border-l-4 border-accent bg-accent/5 p-4 align-top text-sm text-primary">
                        {row.saint}
                      </td>
                      <td className="p-4 align-top text-sm text-muted-foreground">
                        {row.neurostar}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="mt-8 space-y-4 lg:hidden">
              {comparisonRows.map((row, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-border bg-card p-5 shadow-soft"
                >
                  <p className="font-headings text-sm font-bold text-primary">
                    {row.label}
                  </p>
                  <div className="mt-3 space-y-2">
                    <div className="rounded-lg border-l-4 border-accent bg-accent/5 p-3">
                      <p className="text-xs font-bold uppercase tracking-wide text-accent">
                        SAINT®
                      </p>
                      <p className="mt-1 text-sm text-primary">{row.saint}</p>
                    </div>
                    <div className="rounded-lg border-l-4 border-primary/30 bg-muted/30 p-3">
                      <p className="text-xs font-bold uppercase tracking-wide text-primary">
                        NeuroStar®
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {row.neurostar}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Verdict callouts */}
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {verdicts.map((v, i) => {
                const Icon = verdictIcons[i];
                return (
                  <div
                    key={i}
                    className="rounded-2xl border border-border bg-card p-5 shadow-soft"
                  >
                    <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
                      <Icon className="h-5 w-5 text-accent" />
                    </div>
                    <p className="font-headings text-sm font-bold text-primary">
                      {v.title}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {v.text}
                    </p>
                  </div>
                );
              })}
            </div>

            <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
              Remission and response figures come from different study designs
              and populations and are not head-to-head comparisons. Individual
              results vary. Information about NeuroStar® Advanced Therapy is
              drawn from public sources as of September 2026; confirm current
              details with any provider you're considering.
            </p>
          </div>
        </section>

        {/* ── KEY DIFFERENCES ── */}
        <section className="bg-card py-12 sm:py-16">
          <div className="container-px mx-auto max-w-6xl">
            <p className="font-headings text-sm font-bold uppercase tracking-wider text-accent">
              Key Differences
            </p>
            <h2 className="mt-2 font-headings text-2xl font-bold text-primary sm:text-3xl">
              What sets SAINT® apart
            </h2>
            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              {keyDifferences.map((card, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-border bg-background p-6 shadow-soft"
                >
                  <div className="mb-4 h-1 w-12 rounded-full bg-accent" />
                  <h3 className="font-headings text-lg font-bold text-primary">
                    {card.title}
                  </h3>
                  {card.isVisual ? (
                    <>
                      <div className="mt-4">
                        <TreatmentCalendar />
                      </div>
                      <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                        {card.text}
                      </p>
                      {card.footnote && (
                        <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                          {card.footnote}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                      {card.text}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY IT MATTERS ── */}
        <section className="bg-background py-12 sm:py-16">
          <div className="container-px mx-auto max-w-4xl">
            <p className="font-headings text-sm font-bold uppercase tracking-wider text-accent">
              Why It Matters
            </p>
            <h2 className="mt-2 font-headings text-2xl font-bold text-primary sm:text-3xl">
              Five reasons patients pick SAINT® over NeuroStar
            </h2>
            <ol className="mt-8 space-y-5">
              {whyItMatters.map((item, i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-accent-foreground">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-headings text-base font-bold text-primary">
                      {item.title}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                      {item.text}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-8 text-sm text-muted-foreground leading-relaxed">
              SAINT® is cleared for adults 22 and older with major depressive
              disorder who haven't improved on antidepressant medication, and it
              requires an fMRI. If you're outside those criteria — or
              NeuroStar-style once-daily TMS is what your insurance will cover —
              we offer standard TMS in our clinic too, and we'll tell you
              honestly. Either way, your consultation is free.
            </p>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="bg-card py-12 sm:py-16">
          <div className="container-px mx-auto max-w-4xl">
            <p className="font-headings text-sm font-bold uppercase tracking-wider text-accent">
              FAQ
            </p>
            <h2 className="mt-2 font-headings text-2xl font-bold text-primary sm:text-3xl">
              Common questions
            </h2>
            <div className="mt-8 space-y-4">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-border bg-background p-6 shadow-soft"
                >
                  <h3 className="font-headings text-base font-bold text-primary">
                    {faq.q}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <section className="bg-primary py-12 sm:py-16">
          <div className="container-px mx-auto max-w-4xl text-center">
            <p className="font-headings text-sm font-bold uppercase tracking-wider text-accent">
              Free Consultation
            </p>
            <h2 className="mt-2 font-headings text-2xl font-bold text-primary-foreground sm:text-3xl">
              Find out if you're a SAINT® candidate — at no cost
            </h2>
            <p className="mt-4 text-base text-primary-foreground/80 leading-relaxed">
              Tell us a little about your history and our care team will reach
              out within one business day. We'll review your eligibility for
              SAINT®, walk you through the fastest path to treatment, and answer
              every question.
            </p>
            <div className="mt-7 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <button
                onClick={scrollToForm}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-accent px-8 py-3.5 text-base font-bold text-accent-foreground shadow-glow transition-opacity hover:opacity-90"
              >
                Request My Free Consultation
                <ArrowRight className="h-5 w-5" />
              </button>
              <a
                href="tel:9548279793"
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-primary-foreground/30 bg-transparent px-8 py-3.5 text-base font-bold text-primary-foreground transition-colors hover:bg-primary-foreground/10"
              >
                <Phone className="h-5 w-5" />
                954-827-9793
              </a>
            </div>
            <p className="mt-4 text-xs text-primary-foreground/70">
              Same-day or next-day appointments are often available. Self-pay
              patients typically start SAINT® within days.
            </p>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-sm text-primary-foreground/80">
              <MapPin className="h-4 w-4 text-accent" />
              9734 West Sample Road, 2nd Floor, Suite B, Coral Springs, FL 33065
            </p>
          </div>
        </section>

        {/* ── SOURCES ── */}
        <section className="bg-background py-10">
          <div className="container-px mx-auto max-w-4xl">
            <p className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Sources
            </p>
            <ol className="mt-4 space-y-2">
              {sources.map((s) => (
                <li
                  key={s.n}
                  className="flex gap-2 text-sm text-muted-foreground"
                >
                  <span className="font-bold text-primary">{s.n}.</span>
                  <span>
                    {s.text}{" "}
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener"
                      className="text-accent underline underline-offset-2 hover:opacity-80"
                    >
                      {s.url}
                    </a>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ── RELATED PAGES ── */}
        <RelatedPages
          heading="Explore Related Resources"
          links={[
            {
              title: "SAINT® Accelerated TMS",
              href: "/saint",
              description: "FDA-cleared, 5-day fMRI-guided TMS protocol",
            },
            {
              title: "Compare Treatments",
              href: "/compare-treatments",
              description: "Side-by-side matrix of all treatments",
            },
            {
              title: "How SAINT Compares to Other Depression Treatments",
              href: "/how-saint-compares-to-other-depression-treatment-options",
              description: "Broader treatment comparison guide",
            },
            {
              title: "Am I a Candidate?",
              href: "/am-i-a-candidate",
              description: "Screening factors — not disqualifiers",
            },
            {
              title: "Cost & Insurance",
              href: "/cost-insurance",
              description: "Financing options and insurance information",
            },
            {
              title: "Traveling for Treatment",
              href: "/traveling-for-treatment",
              description: "What the week looks like, hotels, what to bring",
            },
          ]}
        />
      </main>
      <Footer />
      {/* SAINT disclaimer + competitor notice in footer area */}
      <div className="bg-primary px-4 pb-8">
        <div className="container-px mx-auto max-w-7xl">
          <SaintDisclaimer extraNotice={competitorNotice} />
        </div>
      </div>
    </>
  );
};

export default SaintVsNeurostar;
